import {
  BulkCreateObservationRequest,
  BulkPointToSpeciesRequest,
  BulkReviewActionResponse,
  ReviewActionResponse,
} from "../components/common/ObjectTypes";
import { ObservationUpdateRequest } from "../components/common/QueryTypes";
import {
  EditFields,
  buildAcceptRequest,
  buildAcceptSelectedRequest,
  buildAssignSpeciesRequest,
  buildEditRequest,
  buildObservationCreateRequest,
  buildObservationUpdateRequest,
  buildRejectRequest,
  buildRejectSelectedRequest,
  newIdempotencyKey,
} from "../components/review/reviewActions";
import {
  AssignSpeciesCardRequest,
  PredictionAppliedAt,
} from "../components/common/QueryTypes";
import axios from "./axios";

// Review commit actions (AKL-682, Photo Review Phase 2 Slice 6).
//
// Routes live under the `/review/*` prefix on the API. Each action commits
// immediately server-side and returns a PARTIAL `ReviewActionResponse` (review
// status + current observations + the touched predictions' latest review) — not
// the full media detail. The single-photo surface therefore re-fetches
// `getMediaDetail` after each action to refresh predictions/suggestions/boxes
// from server truth (it never re-derives aggregation).

/**
 * Accept a suggestion cluster or a single bounding box (§5.1.1 / §5.2.3).
 * @param mediaId the media asset id
 * @param predictionIds the cluster's underlying predictions (one for an object)
 * @param taxonId the resolved target taxon
 * @param appliedAt aggregate (card) or object (single box) precision
 * @param parentTaxonId the parent card's taxon, for object actions (optional)
 * @returns the partial post-action state
 */
export async function acceptSuggestion(
  mediaId: number,
  predictionIds: number[],
  taxonId: number,
  appliedAt: PredictionAppliedAt,
  parentTaxonId?: number | null,
): Promise<ReviewActionResponse> {
  try {
    const resp = await axios.post(
      "/review/suggestions/accept",
      buildAcceptRequest(
        mediaId,
        predictionIds,
        taxonId,
        appliedAt,
        newIdempotencyKey(),
        parentTaxonId,
      ),
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Accept every pending suggestion on the photo AND mark it reviewed, in one
 * transactional request (AKL-752).
 *
 * Not the same as calling `acceptSuggestion` once per card. The endpoint derives
 * the pending set server-side from the same aggregation that produced the cards
 * on screen — the body carries no prediction ids — accepts them all at aggregate
 * precision, and sets the review status, all in one transaction. A failure part
 * way through leaves the photo exactly as it was, so there is no half-applied
 * outcome to reconcile client-side.
 *
 * Returns the same partial `ReviewActionResponse` as the other commits, so the
 * caller still re-fetches the detail afterwards — once, not once per card.
 *
 * Can return HTTP 422 with `detail.error_code = "zero_observations"`, the same
 * structured error `saveMedia` returns: reachable only when the pending set is
 * already empty (a concurrent reviewer got there first) and the photo has no
 * observations, in which case the caller shows the Mark-as-Blank-or-Cancel
 * prompt rather than a raw error.
 * @param mediaId the media asset id
 * @returns the partial post-action state
 */
export async function acceptAllSuggestions(
  mediaId: number,
): Promise<ReviewActionResponse> {
  try {
    const resp = await axios.post("/review/suggestions/accept-all", {
      media_id: mediaId,
      idempotency_key: newIdempotencyKey(),
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Point several suggestion cards within one photo at a single species, creating
 * ONE observation that covers every bounding box in those cards (AKL-774).
 *
 * **One request, not one per card.** Do not "simplify" this into a loop over
 * `editSuggestion` - that fan-out is the AKL-751 bug, and here it would also be
 * wrong on its own terms: per-card requests can half-apply, leaving the photo
 * with some cards moved and some not, and would produce one observation per card
 * rather than the single grouped observation this whole feature exists to make.
 *
 * Each card carries its own `original_taxon_id`, so the server records
 * `accepted` for cards already at the chosen taxon and `edited` for the rest,
 * while the reviewer sees one observation. Pending and rejected cards may be
 * mixed freely.
 *
 * Can return HTTP 422 with `detail.error_code = "blank_taxon_not_allowed"` when
 * the chosen taxon is the blank taxon - blanking is Mark as blank's job, and
 * doing it here would discard the cards the reviewer did not tick.
 *
 * Returns the same partial `ReviewActionResponse` as the other commits, so the
 * caller re-fetches the detail afterwards - once, not once per card.
 *
 * **The idempotency key is the caller's, unlike every other action here**, which
 * mints one per call. This flow deliberately keeps the selection after a failure
 * so the reviewer can retry, and a fresh key on each attempt would defeat the
 * server's replay guard: an ambiguous timeout where the first request actually
 * committed would then commit a second re-action rather than replaying. The
 * surface mints one key when the editor opens and reuses it until the action
 * succeeds or is abandoned.
 * @param mediaId the media asset id
 * @param cards the ticked cards, in panel order
 * @param fields the taxon/count/comments from the observation editor
 * @param idempotencyKey the key for this attempt AND its retries
 * @returns the partial post-action state
 */
export async function assignSpeciesToCards(
  mediaId: number,
  cards: AssignSpeciesCardRequest[],
  fields: EditFields,
  idempotencyKey: string,
): Promise<ReviewActionResponse> {
  try {
    const resp = await axios.post(
      "/review/suggestions/assign-species",
      buildAssignSpeciesRequest(mediaId, cards, fields, idempotencyKey),
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Accept every ticked suggestion card at its OWN species (AKL-779).
 *
 * N cards produce N observations — the deliberate opposite of
 * `assignSpeciesToCards`, which produces one observation across all of them.
 * Both exist because a reviewer needs both answers: "the AI split one animal
 * across these cards" and "the AI got these cards right".
 *
 * One request, never a loop over `acceptSuggestion` — see the AKL-753 note
 * below; the same fan-out rule applies to a selection within one photo.
 * @param mediaId the media asset id
 * @param cards the ticked cards, in panel order
 * @param idempotencyKey retry-safety key
 * @returns the partial review-action response
 */
export async function acceptSelectedCards(
  mediaId: number,
  cards: AssignSpeciesCardRequest[],
  idempotencyKey: string,
): Promise<ReviewActionResponse> {
  try {
    const resp = await axios.post(
      "/review/suggestions/accept-selected",
      buildAcceptSelectedRequest(mediaId, cards, idempotencyKey),
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Reject every ticked suggestion cluster (AKL-779). Creates no observations; a
 * rejected card stays recoverable through the existing re-action path.
 *
 * One request, for the same reason as the accept side.
 * @param mediaId the media asset id
 * @param cards the ticked cards, in panel order
 * @param idempotencyKey retry-safety key
 * @returns the partial review-action response
 */
export async function rejectSelectedCards(
  mediaId: number,
  cards: AssignSpeciesCardRequest[],
  idempotencyKey: string,
): Promise<ReviewActionResponse> {
  try {
    const resp = await axios.post(
      "/review/suggestions/reject-selected",
      buildRejectSelectedRequest(mediaId, cards, idempotencyKey),
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

// Bulk feed actions (AKL-753). Each of the three applies one action across a
// whole hand-picked selection in a SINGLE request and a single server-side
// transaction, returning a `{requested_count, applied_count, skipped[],
// replayed}` summary rather than a per-photo `ReviewActionResponse`.
//
// Do not "simplify" any of these into a loop or a Promise.all over the existing
// single-photo functions. That fan-out is precisely the bug AKL-751 was — one tab
// firing thousands of concurrent requests, some failing at the network layer —
// and what AKL-752 moved the single-photo accept-all away from. A batch must
// either land whole or not at all, which only the server can guarantee.

/**
 * Mark every selected photo blank: reject its unactioned AI predictions, clear
 * its non-blank observations, write a blank observation, set it Reviewed.
 *
 * Photos already carrying a blank observation come back in `skipped` with reason
 * `already_blank` — re-blanking one would leave it with two blank observations.
 * @param mediaIds the selected media asset ids
 * @returns the batch summary
 */
export async function bulkMarkBlank(
  mediaIds: number[],
): Promise<BulkReviewActionResponse> {
  try {
    const resp = await axios.post("/review/bulk/mark-blank", {
      media_ids: mediaIds,
      idempotency_key: newIdempotencyKey(),
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Accept every pending AI suggestion across the selected photos and set each to
 * Reviewed.
 *
 * A photo with nothing pending and no observations to make it reviewable comes
 * back in `skipped` with reason `nothing_to_accept`; one with nothing pending but
 * existing observations is still marked Reviewed, which is what the confirmation
 * promises. Exclusions and out-of-list predictions are never touched.
 * @param mediaIds the selected media asset ids
 * @returns the batch summary
 */
export async function bulkAcceptSuggestions(
  mediaIds: number[],
  setReviewed: boolean,
): Promise<BulkReviewActionResponse> {
  try {
    const resp = await axios.post("/review/bulk/accept-suggestions", {
      media_ids: mediaIds,
      set_reviewed: setReviewed,
      idempotency_key: newIdempotencyKey(),
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Create the same manual observation on every selected photo, and set each to
 * Reviewed only when `set_reviewed` says so.
 *
 * Nothing is ever skipped. Can return HTTP 422 with
 * `detail.error_code = "blank_taxon_not_allowed"` when the chosen taxon is the
 * blank taxon: blanking a whole selection because of the species picked is too
 * surprising to happen implicitly, and Mark as blank is its own action.
 *
 * Mints its own idempotency key per call, like its two siblings — `body` carries
 * no key, so there is nothing here that looks like caller control but isn't.
 * @param body the selection plus the observation fields and the reviewed flag
 * @returns the batch summary
 */
export async function bulkCreateObservation(
  body: BulkCreateObservationRequest,
): Promise<BulkReviewActionResponse> {
  try {
    const resp = await axios.post("/review/bulk/observations", {
      ...body,
      idempotency_key: newIdempotencyKey(),
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Point every selected photo at one species (AKL-811).
 *
 * The four outcomes, which are exactly what the modal's bullets promise — keep
 * the two in step:
 *
 * | Photo has                                            | Result                                                                    |
 * | ---------------------------------------------------- | ------------------------------------------------------------------------- |
 * | Unreviewed animal predictions, no saved observations | Those predictions point to the chosen species                             |
 * | Nothing at all                                       | A fresh observation of the chosen species                                 |
 * | Saved observations **and** unreviewed predictions     | Saved observations untouched; the predictions point to the chosen species |
 * | Saved observations, no unreviewed predictions        | Skipped, reason `already_recorded`                                        |
 *
 * People, vehicles and blanks the AI found are never touched.
 *
 * **One request for the whole selection, never a loop** over
 * `assignSpeciesToCards` or `createObservation`. That fan-out is the AKL-751
 * bug, and here it would also half-apply an action that has no undo.
 *
 * The body carries no `individual_count`: the server derives the count per photo
 * from the number of distinct bounding boxes the re-pointed predictions cover
 * (1 for a photo the AI found nothing in). See `BulkPointToSpeciesRequest`.
 *
 * Can return HTTP 422 with `detail.error_code = "blank_taxon_not_allowed"` when
 * the chosen taxon is the blank taxon — Mark as blank is its own action.
 *
 * Mints its own idempotency key per call, like its three siblings.
 * @param body the selection plus the taxon, comments and the reviewed flag
 * @returns the batch summary
 */
export async function bulkPointToSpecies(
  body: BulkPointToSpeciesRequest,
): Promise<BulkReviewActionResponse> {
  try {
    const resp = await axios.post("/review/bulk/point-to-species", {
      ...body,
      idempotency_key: newIdempotencyKey(),
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Reject a suggestion cluster or a single bounding box (§5.1.2 / §5.2.1).
 * @param mediaId the media asset id
 * @param predictionIds the cluster's underlying predictions (one for an object)
 * @param appliedAt aggregate (card) or object (single box) precision
 * @param parentTaxonId the card's / parent card's taxon (optional)
 * @returns the partial post-action state
 */
export async function rejectSuggestion(
  mediaId: number,
  predictionIds: number[],
  appliedAt: PredictionAppliedAt,
  parentTaxonId?: number | null,
): Promise<ReviewActionResponse> {
  try {
    const resp = await axios.post(
      "/review/suggestions/reject",
      buildRejectRequest(
        mediaId,
        predictionIds,
        appliedAt,
        newIdempotencyKey(),
        parentTaxonId,
      ),
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Edit a suggestion cluster or a single bounding box — new taxon / count /
 * comments (§5.1.3 / §5.2.2).
 * @param mediaId the media asset id
 * @param predictionIds the cluster's underlying predictions (one for an object)
 * @param originalTaxonId the card's current resolved taxon, or null for an
 *   object edit of an `unresolved` box (AKL-717) — a detection with no species
 *   to start from; the API accepts null for object scope only, and only with a
 *   new taxon in `fields`
 * @param appliedAt aggregate (card) or object (single box) precision
 * @param fields the new taxon/count/comments from the editor
 * @param originalCount the count the editor opened with (card's displayed count)
 * @param parentTaxonId the parent card's taxon, for object actions (optional)
 * @returns the partial post-action state
 */
export async function editSuggestion(
  mediaId: number,
  predictionIds: number[],
  originalTaxonId: number | null,
  appliedAt: PredictionAppliedAt,
  fields: EditFields,
  originalCount: number | null,
  parentTaxonId?: number | null,
): Promise<ReviewActionResponse> {
  try {
    const resp = await axios.post(
      "/review/suggestions/edit",
      buildEditRequest(
        mediaId,
        predictionIds,
        originalTaxonId,
        appliedAt,
        fields,
        originalCount,
        newIdempotencyKey(),
        parentTaxonId,
      ),
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Create a manual observation (§5.3.1). Used for both Add Observation and Mark
 * as Unknown (Unknown = the WI Unknown taxon chosen in the picker; no fan-out).
 * observation_type is derived server-side from the taxon's WI category.
 * @param mediaId the media asset id
 * @param fields the taxon/count/comments
 * @returns the partial post-action state
 */
export async function createObservation(
  mediaId: number,
  fields: EditFields,
): Promise<ReviewActionResponse> {
  try {
    const resp = await axios.post(
      "/review/observations",
      buildObservationCreateRequest(mediaId, fields, newIdempotencyKey()),
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Edit an existing observation in place (§5.3.2).
 * @param observationId the observation id
 * @param body the changed fields
 * @returns the partial post-action state
 */
export async function updateObservation(
  observationId: number,
  body: ObservationUpdateRequest,
): Promise<ReviewActionResponse> {
  try {
    const resp = await axios.patch(
      `/review/observations/${observationId}`,
      body,
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/** Build a patch body from editor fields (convenience for `updateObservation`). */
export { buildObservationUpdateRequest };

/**
 * Soft-delete an existing observation (§6.2.4).
 * @param observationId the observation id
 * @returns the partial post-action state
 */
export async function deleteObservation(
  observationId: number,
): Promise<ReviewActionResponse> {
  try {
    const resp = await axios.delete(`/review/observations/${observationId}`);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Mark the photo blank with rejection fan-out over unactioned predictions
 * (§5.3.3). Implicitly commits the photo as reviewed.
 * @param mediaId the media asset id
 * @returns the partial post-action state
 */
export async function markBlank(
  mediaId: number,
): Promise<ReviewActionResponse> {
  try {
    const resp = await axios.post("/review/mark-blank", {
      media_id: mediaId,
      idempotency_key: newIdempotencyKey(),
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * Save / Save & Next commit (§5.4). Flips review_status to reviewed when at
 * least one observation exists; returns HTTP 422 (detail.error_code =
 * "zero_observations") when the photo has zero observations, which the caller
 * turns into the Mark-as-Blank-or-Cancel prompt. The global error handler is
 * NOT skipped: the interceptor rejects 4xx (incl. the 422, and a 403 since
 * AKL-939) without toasting, while 401 (token clear + redirect) and
 * network/timeout keep their normal handling. 5xx also keeps its normal
 * `/unexpectedError` redirect
 * unless the caller passes `suppressUnexpectedErrorRedirect: true` (AKL-706),
 * in which case a 5xx instead rejects locally with the same normalized
 * `{status, data}` shape 4xx already gets, letting the caller handle it
 * in place. Sends an idempotency key (like every other write action) so a
 * double-click / retry can't double-commit.
 * @param mediaId the media asset id
 * @param options.suppressUnexpectedErrorRedirect when true, a 5xx response
 *   rejects locally instead of hard-navigating to `/unexpectedError`
 * @returns the partial post-action state
 */
export async function saveMedia(
  mediaId: number,
  options?: { suppressUnexpectedErrorRedirect?: boolean },
): Promise<ReviewActionResponse> {
  try {
    const resp = await axios.post(
      "/review/save",
      {
        media_id: mediaId,
        idempotency_key: newIdempotencyKey(),
      },
      {
        suppressUnexpectedErrorRedirect:
          options?.suppressUnexpectedErrorRedirect,
      },
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}
